/**
 * Database migrations card.
 *
 * Backend shape:
 *   {
 *     latest_applied: { version: string, name: string, applied_at: string } | null,
 *     failed_count_7d: number,
 *   }
 */
import { formatDate, formatNumber } from '@/lib/utils';
import { TileMeta, TileUnavailable, isUnavailable, tileShell } from './TileStatus';

interface Data extends TileMeta {
  latest_applied: { version: string; name: string; applied_at: string } | null;
  failed_count_7d: number;
}

export function MigrationsCard({ data }: { data: Data }) {
  const unavailable = isUnavailable(data);
  const failed = data?.failed_count_7d ?? 0;
  const hasFailed = !unavailable && failed > 0;
  return (
    <section
      className={`border rounded-lg p-6 ${tileShell(
        unavailable,
        hasFailed ? 'border-amber-500/50 bg-amber-500/5' : 'bg-card',
      )}`}
    >
      <h3 className="text-lg font-semibold mb-3">Database migrations</h3>
      {unavailable && <TileUnavailable error={data?.error} />}
      {unavailable ? null : data?.latest_applied ? (
        <div className="text-sm space-y-1">
          <div>
            <span className="text-muted-foreground">Latest applied:</span>{' '}
            <span className="font-mono font-medium">{data.latest_applied.name}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            version {data.latest_applied.version} at{' '}
            {formatDate(data.latest_applied.applied_at)}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No migration records.</p>
      )}
      <div className="mt-3 text-sm">
        <span className="text-muted-foreground">Failed in last 7 days: </span>
        <span className={hasFailed ? 'text-amber-700 font-semibold' : 'font-medium'}>
          {unavailable ? '—' : formatNumber(failed)}
        </span>
      </div>
    </section>
  );
}
